import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { filterSuggestionItems } from '@blocknote/core';
import {
  BasicTextStyleButton,
  BlockTypeSelect,
  CreateLinkButton,
  FormattingToolbar,
  FormattingToolbarController,
  LinkToolbarController,
  SuggestionMenuController,
  useCreateBlockNote,
  useEditorChange,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import type { Middleware } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';

import {
  createMediaStrippingPasteHandler,
  parseMarkdownToBlocks,
  trimMarkdown,
} from '@/components/atoms/Editors/BlockNoteEditor/editorUtils';
import { cn } from '@/lib/utils';
import { Option } from '@/shared/types/rules';

import { sharedEditorSchema } from './schema';
import { variableHighlightPlugin, variableHighlightPluginKey } from './variableHighlightPlugin';
import {
  getFilteredBlockTypeItems,
  getSlashMenuItems,
  getVariableSuggestionItems,
} from './variableUtils';

import '@blocknote/shadcn/style.css';
import './BlockNoteEditor.scss';

type FormattingButton = 'bold' | 'italic' | 'code' | 'link' | 'blockType';

export interface BlockNoteEditorProps {
  content: string | null | undefined;
  onContentChange: (content: string) => void;
  variableOptions: Option[];
  placeholder?: string;
  /**
   * When true, the placeholder is shown only while the whole document is empty
   * (BlockNote's `emptyDocument` placeholder) instead of on every focused empty
   * block (the `default` placeholder). Use for single-value fields so pressing
   * Enter to add a line does not surface the placeholder on the new empty line.
   */
  placeholderOnlyWhenEmpty?: boolean;
  formattingButtons?: FormattingButton[];
  toolbarFloatingOptions?: { middleware?: Middleware[]; strategy?: 'absolute' | 'fixed' };
  linkToolbarFloatingOptions?: { middleware?: Middleware[]; strategy?: 'absolute' | 'fixed' };
  allowedSlashKeys?: string[];
  allowedBlockTypeKeys?: string[];
  maxLength?: number;
  enforceMaxLength?: boolean;
  className?: string;
  editable?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Accessibility: indicates invalid state for form validation */
  'aria-invalid'?: boolean | 'true' | 'false';
}

/**
 * Shared BlockNote editor component with configurable toolbars and features
 * Supports context variables, slash commands, and Markdown content
 */
export function BlockNoteEditor({
  content,
  onContentChange,
  variableOptions,
  placeholder = '',
  placeholderOnlyWhenEmpty = false,
  formattingButtons = ['bold', 'italic', 'link'],
  toolbarFloatingOptions,
  linkToolbarFloatingOptions,
  allowedSlashKeys,
  allowedBlockTypeKeys,
  maxLength,
  enforceMaxLength = true,
  className = '',
  editable = true,
  onFocus,
  onBlur,
  'aria-invalid': ariaInvalid,
}: BlockNoteEditorProps) {
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);
  const lastInitializedContentRef = useRef<string | null>(null);
  const lastValidContentRef = useRef<string | null>(null);
  const lastVariableOptionsRef = useRef<Option[]>(variableOptions);

  // Create editor instance with custom schema
  const editor = useCreateBlockNote({
    placeholders: placeholderOnlyWhenEmpty
      ? { emptyDocument: placeholder }
      : { default: placeholder },
    schema: sharedEditorSchema,
    pasteHandler: createMediaStrippingPasteHandler(),
    trailingBlock: false,
  });

  // Register variable highlight plugin
  useEffect(() => {
    const tiptap = editor._tiptapEditor;
    tiptap.registerPlugin(variableHighlightPlugin);
    return () => {
      tiptap.unregisterPlugin(variableHighlightPluginKey);
    };
  }, [editor]);

  // Function to load content from markdown
  const loadContent = useCallback(
    async (currentContent: string | null | undefined) => {
      try {
        const blocks = await parseMarkdownToBlocks(editor, currentContent || '');
        editor.replaceBlocks(editor.document, blocks);
      } catch (error) {
        console.error('Failed to parse markdown:', error);
      }
    },
    [editor]
  );

  // Initialize editor content from markdown
  useEffect(() => {
    const contentChanged = lastInitializedContentRef.current !== content;
    const variableOptionsChanged =
      JSON.stringify(lastVariableOptionsRef.current) !== JSON.stringify(variableOptions);
    const shouldInitialize = !isInitialized || contentChanged || variableOptionsChanged;

    if (!shouldInitialize) {
      return;
    }

    const initializeEditor = async () => {
      await loadContent(content);
      // Store the original content prop for comparison (to detect external changes)
      lastInitializedContentRef.current = content;
      // Get normalized markdown for length checking and as the valid content baseline
      let normalizedMarkdown = await editor.blocksToMarkdownLossy(editor.document);
      normalizedMarkdown = trimMarkdown(normalizedMarkdown);
      // Only set as valid if within max length limit to avoid infinite loop
      // when reverting oversized pre-existing content. Explicitly clear to avoid
      // stale content from previous page being used as revert target.
      const isWithinLimit =
        !enforceMaxLength || !maxLength || normalizedMarkdown.length <= maxLength;
      lastValidContentRef.current = isWithinLimit ? normalizedMarkdown : null;
      lastVariableOptionsRef.current = variableOptions;
      setIsInitialized(true);
    };

    initializeEditor();
  }, [editor, content, loadContent, isInitialized, variableOptions, enforceMaxLength, maxLength]);

  // Convert variable options to suggestion items
  const variableSuggestionItems = useMemo(
    () => getVariableSuggestionItems(editor, variableOptions, t),
    [editor, variableOptions, t]
  );

  // Get slash menu items
  const slashMenuItems = useMemo(
    () => getSlashMenuItems(editor, allowedSlashKeys),
    [editor, allowedSlashKeys]
  );

  // Get filtered block type items for the formatting toolbar dropdown
  const blockTypeItems = useMemo(
    () => getFilteredBlockTypeItems(editor, allowedBlockTypeKeys),
    [editor, allowedBlockTypeKeys]
  );

  // Handle editor changes - no live normalization (like guidelines editor)
  useEditorChange(
    useCallback(
      async (editor) => {
        if (!isInitialized) {
          return;
        }

        try {
          // Convert to Markdown
          let markdown = await editor.blocksToMarkdownLossy(editor.document);
          markdown = trimMarkdown(markdown);

          // Check max length if enforced (skip if no valid content to revert to,
          // which means we started with oversized content - allow free editing)
          if (
            enforceMaxLength &&
            maxLength &&
            markdown.length > maxLength &&
            lastValidContentRef.current !== null
          ) {
            // Revert to last valid content
            const validBlocks = await parseMarkdownToBlocks(editor, lastValidContentRef.current);
            editor.replaceBlocks(editor.document, validBlocks);
            return;
          }

          // Update the ref to prevent re-initialization
          lastInitializedContentRef.current = markdown;
          // Only update valid ref when within limit to maintain the invariant
          // that lastValidContentRef is always within limit (or null)
          if (!enforceMaxLength || !maxLength || markdown.length <= maxLength) {
            lastValidContentRef.current = markdown;
          }

          onContentChange(markdown);
        } catch (error) {
          console.error('Failed to process editor change:', error);
        }
      },
      [isInitialized, onContentChange, maxLength, enforceMaxLength]
    ),
    editor
  );

  // Render formatting toolbar buttons based on config
  const renderFormattingButtons = () => {
    return formattingButtons.map((button) => {
      const key = `${button}Button`;
      switch (button) {
        case 'bold':
          return <BasicTextStyleButton basicTextStyle="bold" key={key} />;
        case 'italic':
          return <BasicTextStyleButton basicTextStyle="italic" key={key} />;
        case 'code':
          return <BasicTextStyleButton basicTextStyle="code" key={key} />;
        case 'link':
          return <CreateLinkButton key={key} />;
        case 'blockType':
          return <BlockTypeSelect key={key} items={blockTypeItems} />;
        default:
          return null;
      }
    });
  };

  return (
    <div className={cn('blocknote-editor', className)} aria-invalid={ariaInvalid}>
      <BlockNoteView
        theme="light"
        editor={editor}
        formattingToolbar={false}
        sideMenu={false}
        slashMenu={false}
        linkToolbar={false}
        editable={editable}
        onFocus={onFocus}
        onBlur={onBlur}
        data-theming-css-blocknote
      >
        <SuggestionMenuController
          triggerCharacter="$"
          getItems={async (query) => filterSuggestionItems(variableSuggestionItems, query)}
        />
        <SuggestionMenuController
          triggerCharacter="/"
          getItems={async (query) => filterSuggestionItems(slashMenuItems, query)}
        />
        <FormattingToolbarController
          formattingToolbar={() => (
            <FormattingToolbar>{renderFormattingButtons()}</FormattingToolbar>
          )}
          floatingOptions={toolbarFloatingOptions}
        />
        <LinkToolbarController floatingOptions={linkToolbarFloatingOptions} />
      </BlockNoteView>
    </div>
  );
}
